import { useId } from "react";
import type { AngerCurve } from "../lib/types";
import { useDrawOn } from "../lib/useMotion";

// 빡침 곡선 (핸드오프 AngerCurve). 스무딩 경로 + 해칭 채움 + 피크 마커. 팝 컬러.
function smoothPath(pts: { x: number; y: number }[]): string {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    const mx = (p0.x + p1.x) / 2;
    d += ` Q ${p0.x.toFixed(1)} ${p0.y.toFixed(1)} ${mx.toFixed(1)} ${((p0.y + p1.y) / 2).toFixed(1)}`;
    d += ` Q ${p1.x.toFixed(1)} ${p1.y.toFixed(1)} ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`;
  }
  return d;
}

interface Props {
  curve: AngerCurve;
  color?: string;
  w?: number;
  h?: number;
  hatch?: boolean;
  showPeak?: boolean;
  showAxis?: boolean;
  animate?: boolean;
}

export default function AngerCurveViz({
  curve,
  color = "#fff",
  w = 320,
  h = 120,
  hatch = false,
  showPeak = false,
  showAxis = true,
  animate = false,
}: Props) {
  const uid = useId().replace(/[:]/g, "");
  const svgRef = useDrawOn<SVGSVGElement>(animate, 1100);
  const pad = 10;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;
  const data = curve.points;
  const n = data.length;
  const pts = data.map((v, i) => ({
    x: pad + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW),
    y: pad + (1 - Math.max(0, Math.min(1, v))) * innerH,
  }));
  const d = smoothPath(pts);
  const areaPath = `${d} L ${(w - pad).toFixed(1)} ${(h - pad).toFixed(1)} L ${pad.toFixed(1)} ${(h - pad).toFixed(1)} Z`;
  let peakI = 0;
  data.forEach((v, i) => {
    if (v > data[peakI]) peakI = i;
  });
  const peak = pts[peakI];

  return (
    <svg ref={svgRef} width="100%" viewBox={`0 0 ${w} ${h}`} fill="none" style={{ display: "block", overflow: "visible" }}>
      {hatch && (
        <defs>
          <clipPath id={`clip${uid}`}>
            <path d={areaPath} />
          </clipPath>
        </defs>
      )}
      {hatch && (
        <g clipPath={`url(#clip${uid})`} stroke={color} strokeWidth="1" opacity="0.5">
          {Array.from({ length: Math.ceil((w + innerH) / 7) }).map((_, i) => {
            const x = pad - innerH + i * 7;
            return <line key={i} x1={x} y1={h - pad} x2={x + innerH} y2={pad} />;
          })}
        </g>
      )}
      {showAxis && <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke={color} strokeWidth="1" opacity="0.28" />}
      <path data-draw d={d} stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      {showPeak && (
        <g>
          <line x1={peak.x} y1={peak.y} x2={peak.x} y2={h - pad} stroke={color} strokeWidth="1" strokeDasharray="2 4" opacity="0.4" />
          <circle cx={peak.x} cy={peak.y} r="3.8" fill="#000" stroke={color} strokeWidth="1.8" />
        </g>
      )}
    </svg>
  );
}
